import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import { supabase } from '@/lib/supabase';

interface DashboardStats {
  productCount: number;
  memberCount: number;
  paidOrderCount: number;
  paidMemberCount: number;
  pendingShipCount: number;
  afterSaleCount: number;
  pendingPayCount: number;
  soldOutCount: number;
}

interface RecentOrder {
  id: string;
  order_no: string;
  total_amount: number;
  status: string;
  created_at: string;
  profiles?: { nickname: string | null } | null;
}

const statusColor: Record<string, string> = {
  '待发货': 'bg-amber-50 text-amber-600',
  '已完成': 'bg-emerald-50 text-emerald-600',
  '售后中': 'bg-rose-50 text-rose-600',
  '待付款': 'bg-gray-100 text-gray-500',
  '待收货': 'bg-sky-50 text-sky-600',
};

const quickActions = [
  { label: '发布商品', icon: 'ri-add-box-line', color: 'bg-emerald-500', path: '/admin/products' },
  { label: '店铺装修', icon: 'ri-paint-brush-line', color: 'bg-rose-500', path: '/admin/shop' },
  { label: '会员管理', icon: 'ri-user-3-line', color: 'bg-sky-500', path: '/admin/members' },
  { label: '分销中心', icon: 'ri-share-line', color: 'bg-emerald-600', path: '/admin/marketing' },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    productCount: 0, memberCount: 0, paidOrderCount: 0, paidMemberCount: 0,
    pendingShipCount: 0, afterSaleCount: 0, pendingPayCount: 0, soldOutCount: 0,
  });
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const [
      { count: productCount },
      { count: memberCount },
      { count: paidOrderCount },
      { count: pendingShipCount },
      { count: afterSaleCount },
      { count: pendingPayCount },
      { count: soldOutCount },
      { data: ordersData },
    ] = await Promise.all([
      supabase.from('products').select('*', { count: 'exact', head: true }),
      supabase.from('custom_users').select('*', { count: 'exact', head: true }),
      supabase.from('orders').select('*', { count: 'exact', head: true }).neq('status', '待付款').neq('status', '已取消'),
      supabase.from('orders').select('*', { count: 'exact', head: true }).eq('status', '待发货'),
      supabase.from('orders').select('*', { count: 'exact', head: true }).eq('status', '售后中'),
      supabase.from('orders').select('*', { count: 'exact', head: true }).eq('status', '待付款'),
      supabase.from('products').select('*', { count: 'exact', head: true }).eq('status', '售罄'),
      supabase.from('orders').select('id, order_no, total_amount, status, created_at, profiles(nickname)').order('created_at', { ascending: false }).limit(5),
    ]);

    // 付款会员数：有过付款订单的不重复用户数
    const { data: paidUsers } = await supabase
      .from('orders')
      .select('user_id')
      .neq('status', '待付款')
      .neq('status', '已取消');
    const paidMemberCount = new Set((paidUsers ?? []).map((o: { user_id: string }) => o.user_id)).size;

    setStats({
      productCount: productCount ?? 0,
      memberCount: memberCount ?? 0,
      paidOrderCount: paidOrderCount ?? 0,
      paidMemberCount,
      pendingShipCount: pendingShipCount ?? 0,
      afterSaleCount: afterSaleCount ?? 0,
      pendingPayCount: pendingPayCount ?? 0,
      soldOutCount: soldOutCount ?? 0,
    });
    setRecentOrders((ordersData ?? []) as RecentOrder[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const formatTime = (ts: string) =>
    new Date(ts).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <AdminLayout title="首页">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary stats */}
          <section className="mb-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">数据概况</h2>
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: '商品总数量', value: stats.productCount, icon: 'ri-shopping-bag-3-line', color: 'text-rose-500 bg-rose-50' },
                { label: '会员总人数', value: stats.memberCount, icon: 'ri-group-line', color: 'text-amber-500 bg-amber-50' },
                { label: '付款订单总量', value: stats.paidOrderCount, icon: 'ri-file-list-3-line', color: 'text-emerald-500 bg-emerald-50' },
                { label: '消费总人数', value: stats.paidMemberCount, icon: 'ri-user-heart-line', color: 'text-violet-500 bg-violet-50' },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl p-4 flex items-center gap-4">
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${s.color}`}>
                    <i className={`${s.icon} text-xl`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">{s.label}</p>
                    <p className="text-xl font-bold text-gray-800">{s.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Pending stats */}
          <section className="mb-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">待处理事项</h2>
            <div className="bg-white rounded-xl">
              <div className="grid grid-cols-4 divide-x divide-gray-100">
                {[
                  { label: '待发货订单（笔）', value: stats.pendingShipCount, urgent: stats.pendingShipCount > 0 },
                  { label: '售后单（笔）', value: stats.afterSaleCount, urgent: stats.afterSaleCount > 0 },
                  { label: '待付款订单（笔）', value: stats.pendingPayCount, urgent: false },
                  { label: '已售罄商品', value: stats.soldOutCount, urgent: stats.soldOutCount > 0 },
                ].map((s) => (
                  <div key={s.label} className="flex flex-col items-center py-5 gap-2">
                    <p className="text-sm text-gray-500">{s.label}</p>
                    <p className={`text-2xl font-bold ${s.urgent ? 'text-rose-500' : 'text-gray-800'}`}>{s.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Quick actions */}
          <section className="mb-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">常用功能</h2>
            <div className="bg-white rounded-xl p-5">
              <div className="flex gap-6 flex-wrap">
                {quickActions.map((a) => (
                  <button key={a.label} onClick={() => navigate(a.path)} className="flex items-center gap-2.5 cursor-pointer group">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${a.color} group-hover:opacity-90 transition-opacity`}>
                      <i className={`${a.icon} text-white text-lg`} />
                    </div>
                    <span className="text-sm text-gray-700 whitespace-nowrap">{a.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Recent orders */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700">最新订单</h2>
              <button onClick={() => navigate('/admin/orders')} className="text-xs text-rose-500 cursor-pointer hover:underline whitespace-nowrap">
                查看全部
              </button>
            </div>
            <div className="bg-white rounded-xl overflow-hidden">
              {recentOrders.length === 0 ? (
                <div className="py-16 text-center text-gray-400 text-sm">暂无订单数据</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left text-xs text-gray-400 font-medium px-5 py-3">订单号</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-5 py-3">会员</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-5 py-3">金额</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-5 py-3">状态</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-5 py-3">时间</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-5 py-3">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order, i) => (
                      <tr key={order.id} className={`${i < recentOrders.length - 1 ? 'border-b border-gray-50' : ''} hover:bg-gray-50/50 transition-colors`}>
                        <td className="px-5 py-3.5 text-sm text-gray-600 font-mono">{order.order_no}</td>
                        <td className="px-5 py-3.5 text-sm text-gray-800">{order.profiles?.nickname ?? '未知用户'}</td>
                        <td className="px-5 py-3.5 text-sm font-semibold text-gray-800">¥{Number(order.total_amount).toFixed(2)}</td>
                        <td className="px-5 py-3.5">
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor[order.status] ?? 'bg-gray-100 text-gray-500'}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-400">{formatTime(order.created_at)}</td>
                        <td className="px-5 py-3.5">
                          <button onClick={() => navigate('/admin/orders')} className="text-xs text-rose-500 cursor-pointer hover:underline whitespace-nowrap">
                            查看
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
